import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, Plus, Star, ArrowUp, ArrowDown, CheckCircle, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import axios from "axios";

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  // Add Partner State
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("user_roles") || "[]");
    setIsAdmin(roles.includes("admin"));
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Sort by showcase order (ASCending now to match public) then name
      const sorted = res.data.sort((a, b) => {
        if (a.showcase !== b.showcase) return b.showcase ? 1 : -1;
        if (a.showcase_order !== b.showcase_order) return (a.showcase_order || 0) - (b.showcase_order || 0);
        return a.name.localeCompare(b.name);
      });
      setPartners(sorted);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPartner = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/partners`, {
        name: newName,
        approved: true,
        industries: ["General"],
        regions: ["Global"]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddOpen(false);
      fetchPartners();
      setNewName("");
    } catch (e) {
      console.error(e);
      alert("Failed to add partner");
    } finally {
      setAdding(false);
    }
  };

  const toggleShowcase = async (partner) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/partners/${partner.id}`, {
        showcase: !partner.showcase,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPartners();
    } catch (e) {
      console.error(e);
    }
  };

  const handleApproval = async (partner, status) => {
      if (!window.confirm(`Are you sure you want to ${status ? "APPROVE" : "DENY"} this partner? This will send an email notification.`)) return;
      try {
          const token = localStorage.getItem("token");
          await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/partners/${partner.id}`, {
              approved: status
          }, {
              headers: { Authorization: `Bearer ${token}` }
          });
          alert(`Partner ${status ? "Approved" : "Denied"}`);
          fetchPartners();
      } catch (e) {
          alert("Failed to update status");
      }
  };

  const handleReorder = async (partner, direction) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/partners/${partner.id}/reorder`, {
        direction: direction
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPartners();
    } catch (e) {
      console.error(e);
      alert("Failed to reorder");
    }
  };

  const filtered = partners.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
           <h1 className="text-3xl font-bold tracking-tight">Partner Directory</h1>
           <p className="text-gray-500">Connect with approved economic development partners.</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto items-center">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search partners..." 
              className="pl-8" 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {isAdmin && (
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button size="icon"><Plus className="h-4 w-4" /></Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Partner</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddPartner} className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Partner Name</Label>
                    <Input value={newName} onChange={e => setNewName(e.target.value)} required />
                  </div>
                  <Button type="submit" className="w-full" disabled={adding}>
                    {adding ? "Adding..." : "Add Partner"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading ? (
           <p>Loading directory...</p>
        ) : filtered.length === 0 ? (
           <p className="text-gray-500">No partners found.</p>
        ) : (
          filtered.map((partner) => (
            <Card key={partner.id} className={`flex flex-col hover:shadow-md transition-shadow ${partner.showcase ? "border-yellow-400 border-2" : ""} ${!partner.approved ? "bg-red-50 border-red-200" : ""}`}>
              <CardHeader className="p-0 relative">
                {isAdmin && (
                  <div className="absolute top-2 right-2 flex gap-1 z-10">
                    <Button 
                      size="icon" 
                      variant="secondary" 
                      className={`h-6 w-6 ${partner.showcase ? "bg-yellow-100 text-yellow-600" : "bg-gray-100 text-gray-400"}`}
                      onClick={() => toggleShowcase(partner)}
                      title="Toggle Showcase"
                    >
                      <Star className="h-3 w-3" />
                    </Button>
                    {partner.showcase && (
                      <>
                        <Button 
                          size="icon" 
                          variant="secondary" 
                          className="h-6 w-6 hover:bg-yellow-100" 
                          onClick={() => handleReorder(partner, "up")}
                          title="Move Up"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="secondary" 
                          className="h-6 w-6 hover:bg-yellow-100" 
                          onClick={() => handleReorder(partner, "down")}
                          title="Move Down"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>
                )}
                
                {/* Pending Status Badge */}
                {!partner.approved && (
                    <div className="absolute top-2 left-2 z-10">
                        <Badge variant="destructive">Pending Approval</Badge>
                    </div>
                )}

                <div className="h-32 bg-gray-100 flex items-center justify-center border-b rounded-t-lg relative">
                  {partner.logo_url ? (
                    <img src={partner.logo_url.startsWith('http') ? partner.logo_url : `${process.env.REACT_APP_BACKEND_URL}${partner.logo_url}`} alt={partner.name} className="h-20 object-contain" />
                  ) : (
                    <span className="text-2xl font-bold text-gray-300">{partner.name[0]}</span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 p-5">
                <h3 className="font-bold text-lg mb-2">{partner.name}</h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  {partner.industries.map(i => (
                    <Badge key={i} variant="secondary" className="text-xs">{i}</Badge>
                  ))}
                </div>
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {partner.regions.join(", ") || "Global"}
                </div>
              </CardContent>
              <CardFooter className="p-5 pt-0 flex flex-col gap-2">
                <div className="flex w-full gap-2">
                    <Button className="flex-1" variant="outline" size="sm" asChild>
                    <Link to={`/portal/partners/${partner.id}`}>Profile</Link>
                    </Button>
                    {isAdmin && (
                    <Button className="flex-1" variant="default" size="sm" asChild>
                        <Link to={`/portal/partners/${partner.id}/edit`}>Edit</Link>
                    </Button>
                    )}
                </div>
                
                {/* Admin Approval Buttons */}
                {isAdmin && !partner.approved && (
                    <div className="flex w-full gap-2 mt-2 pt-2 border-t">
                        <Button className="flex-1 bg-green-600 hover:bg-green-700" size="sm" onClick={() => handleApproval(partner, true)}>
                            <CheckCircle className="mr-1 h-3 w-3" /> Approve
                        </Button>
                        <Button className="flex-1" variant="destructive" size="sm" onClick={() => handleApproval(partner, false)}>
                            <XCircle className="mr-1 h-3 w-3" /> Deny
                        </Button>
                    </div>
                )}
              </CardFooter>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Partners;
